import
{
  OutputStructure,
  PaginatedResult,
  PaginationRequest,
  SortedRequest,
  System,
  SystemDetails,
  SystemMigrateRequest,
  SystemEvents,
  SystemLicense,
  SystemMaintenanceReport,
  SystemMeasurements,
  SystemNotifications,
  SystemOperate,
  SystemPermissions,
  SystemPlan,
  SystemRTM,
  SystemShare,
  SystemShareDisplay,
  SystemShareNotifications,
  SystemShareOperate,
  SystemShareType,
  SystemTextMessages,
  SystemTextMessagesDto,
  SystemType,
  SystemElement as _SystemElement,
  SystemGroup as _SystemGroup,
  SystemSetup as _SystemSetup,
  zoneAccessPermitted,
  SystemPanel,
  SystemSpecificationDTO,
} from "@ews/react-data";

import { ReactTopics } from "@ews/websocket-service";

import { useLoadingIndicator } from "../Loading";

import { useTopicData, useWebsocket } from "./ReactDataProvider";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import
{
  AuthorizationLevel,
  EventKindType,
  NoDeviceNumber,
  NumberGroup,
  eventDetails,
  eventDisplay,
  eventPriorityCompare,
  eventRTMCategory,
  isElement,
  isGroup
} from "@ews/zlt-events";

import
{
  getResult,
  groupDisplay,
  isSuccess,
  propertyNotOperable
} from "@ews/zlt-operate";

import
{
  useAuthorization
} from "../Authorization";

import
{
  useIonToast
} from "@ionic/react";

import
{
  useMyProfileId
} from "../Login/LoginProvider";

import
{
  useNamedRoutes
} from "../NamedRoutes";

import
{
  useAlert
} from "../Dialog";

import
{
  useL10n
} from "@ews/react-localization-context";

import
{
  useInactiveInterval
} from "../Interval";

import type { PSU } from "../System/Reports/PowerUnitsDocumentation/types";

import type { SystemElementForm } from "../System/Reports/SystemElementList/types";

import { floatingPromiseHandler } from "../../utils";
import type { SentTextMessage } from "../System/SentTextMessagesLog/types";

export
{
  type PaginatedResult
};

export type SystemElement = _SystemElement & {
  eventKindType?: EventKindType;
  groupText?: string;
  rtm?: SystemRTM[];
  properties?: number;
};

export type SystemGroup = _SystemGroup & {
  elements?: SystemElement[];
  hidden?: boolean;
  operate?: boolean;
  eventKindType?: EventKindType;
  rtm?: SystemRTM[];
};

export type SystemSetup = _SystemSetup & {
  groups: SystemGroup[];
};

export const useCurrentSystem = () =>
{
  const { params } = useNamedRoutes();
  const { id } = params();
  return useSystem(id || "");
};

export const useSystem = (systemId: string) =>
{
  return useTopicData<System>(
    `${ReactTopics.system.withParams({ systemId })}`,
    {} as System
  );
};

export const useSystemDetails = (systemId: string) =>
{
  return useTopicData<SystemDetails>(
    `${ReactTopics.systemDetails.withParams({ systemId })}`,
    {} as SystemDetails
  );
};
const hackObj = {};

export const useSystemMigrateRequest = (systemId: string) =>
{
  return hackObj as SystemMigrateRequest;
  // return useTopicData<SystemMigrateRequest>(
  //   `${ReactTopics.systemMigrateRequest.withParams({ systemId })}`,
  //   {} as SystemMigrateRequest
  // );
};

export const useSystemEvents = (systemId: string) =>
{
  return useTopicData<SystemEvents[]>(
    `${ReactTopics.systemEvents.withParams({ systemId })}`,
    [] as SystemEvents[]
  );
};

export const useSystemPermissions = (systemId: string) =>
{
  return useTopicData<SystemPermissions>(
    `${ReactTopics.systemPermissions.withParams({ systemId })}`,
    {} as SystemPermissions
  );
};

export const useSystemShare = (systemId: string, userId: string) =>
{
  return useTopicData<SystemShare>(
    `${ReactTopics.systemShare.withParams({ systemId, userId })}`,
    {} as SystemShare
  );
};

export const useSystemShareDisplay = (systemId: string, userId: string) =>
{
  return useTopicData<SystemShareDisplay>(
    `${ReactTopics.systemShareDisplay.withParams({ systemId, userId })}`,
    {} as SystemShareDisplay
  );
};
export const useSystemShareOperate = (systemId: string, userId: string) =>
{
  return useTopicData<SystemShareOperate>(
    `${ReactTopics.systemShareOperate.withParams({ systemId, userId })}`,
    {} as SystemShareOperate
  );
};
export const useSystemShareNotifications = (
  systemId: string,
  userId: string
) =>
{
  return useTopicData<SystemShareNotifications>(
    `${ReactTopics.systemShareNotifications.withParams({ systemId, userId })}`,
    {} as SystemShareNotifications
  );
};

export const useSystemLicense = (systemId: string) =>
{
  return useTopicData<SystemLicense>(
    `${ReactTopics.systemLicense.withParams({ systemId })}`,
    {} as SystemLicense
  );
};

export const useSystemNotifications = (systemId: string) =>
{
  return useTopicData<SystemNotifications>(
    `${ReactTopics.systemNotifications.withParams({ systemId })}`,
    {} as SystemNotifications
  );
};

export const useSystemMaintenance = (systemId: string) =>
{
  return useTopicData<SystemMaintenanceReport>(
    `${ReactTopics.systemMaintenance.withParams({ systemId })}`,
    {} as SystemMaintenanceReport
  );
};

export const useSystemSpecification = (systemId: string) =>
{
  return useTopicData<SystemSpecificationDTO>(
    `${ReactTopics.systemSpecification.withParams({ systemId })}`,
    {} as SystemSpecificationDTO
  );
};


const removeHiddenRTMEventFilter = (authorizationLevel: AuthorizationLevel, systemShareDisplay: SystemShareDisplay) =>
{
  return (rtm: SystemRTM) =>
  {
    const { eventKindType, properties } = rtm;
    const details = eventDetails(eventKindType);
    return (
      Boolean(details) &&
      eventDisplay(properties || 0, authorizationLevel) &&
      zoneAccessPermitted(rtm.zone, rtm.numberGroup, systemShareDisplay)
    );

  };
};

const removeRTMZoneEventsWithElementEvent = (rtm: SystemRTM[]) =>
{

  const zonesWithElementEvent: Record<string, true> = {};
  return rtm.map((rtm) =>
  {
    const { numberGroup, zone, element, eventKindType } = rtm;
    const category = eventRTMCategory(eventKindType);

    if (isElement({ zone, element })) {
      zonesWithElementEvent[`${numberGroup}x${zone}x${category}`] = true;
    }

    return rtm;

  }).filter((rtm) =>
  {
    const { numberGroup, zone, element, eventKindType } = rtm;
    const category = eventRTMCategory(eventKindType);
    return (isElement({ zone, element }) || !zonesWithElementEvent[`${numberGroup}x${zone}x${category}`]);
  });
};

export function useSystemRTM(systemId: string, group?: false): SystemRTM[];
export function useSystemRTM(
  systemId: string,
  group: true
): Record<string, SystemRTM[]>;
export function useSystemRTM(systemId: string, group: Boolean = false)
{
  const { authorizationLevel } = useAuthorization();
  const me = useMyProfileId();

  const systemShareDisplay = useSystemShareDisplay(systemId, me || "");

  const rtmTopicData = useTopicData<SystemRTM[]>(
    `${ReactTopics.systemRTM.withParams({ systemId })}`,
    []
  );

  const rtm = useMemo(() =>
  {
    return removeRTMZoneEventsWithElementEvent(rtmTopicData)
      .filter(removeHiddenRTMEventFilter(authorizationLevel, systemShareDisplay));

  }, [authorizationLevel, rtmTopicData, systemShareDisplay]);

  const tableOrRtm: Record<string, SystemRTM[]> | SystemRTM[] = useMemo(() =>
  {
    if (!group) return rtm;

    const table: Record<string, SystemRTM[]> = {};

    for (const item of rtm) {
      const { numberGroup, zone, element } = item;

      table[`${numberGroup}x${zone}`] = table[`${numberGroup}x${zone}`] || [];

      if (isGroup({ zone: zone!, element: element! })) {
        table[`${numberGroup}x${zone}`] = table[`${numberGroup}x${zone}`] || [];
        table[`${numberGroup}x${zone}`].push(item);
      } else {
        table[`${numberGroup}x${zone}x${element}`] =
          table[`${numberGroup}x${zone}x${element}`] || [];
        table[`${numberGroup}x${zone}x${element}`].push(item);
      }
    }

    return table;
  }, [group, rtm]);

  return tableOrRtm;
}

// const displayByShare = (group: SystemGroup, share: any) =>
// {

//     const id = group.id;

//     switch (group.numberGroup) {
//         case NumberGroup.Zone:
//             return share.fromZone <= id && share.toZone >= id;
//         case NumberGroup.ActuationDevice:
//             return share.fromActuation <= id && share.toActuation >= id;
//         case NumberGroup.AlarmingDevice:
//             return share.fromAlarmingDevice <= id && share.toAlarmingDevice >= id;
//         case NumberGroup.TransmissionDevice:
//             return share.fromTransmissionDevice <= id && share.toTransmissionDevice >= id;
//     }

//     return false;

// };

export const useSystemSetup = (systemId: string) =>
{
  const { authorizationLevel } = useAuthorization();
  const rtm = useSystemRTM(systemId, true);
  const me = useMyProfileId();

  /* const systemShareDisplay = useSystemShareDisplay(systemId, me || ""); */
  const systemShareOperate = useSystemShareOperate(systemId, me || "");

  const setup: SystemSetup = useTopicData<SystemSetup>(
    `${ReactTopics.systemSetup.withParams({ systemId })}`,
    { groups: [] as SystemGroup[] } as SystemSetup
  );

  for (const group of setup.groups) {
    const zone = group.id!;
    const numberGroup = group.numberGroup!;

    const groupProperties = group.properties1!;

    const address = {
      zone: group.id!,
      element: NoDeviceNumber,
    };

    group.hidden =
      !groupDisplay(
        group.numberGroup!,
        group.properties1 || 0,
        authorizationLevel,
        address
      ) || !zoneAccessPermitted(zone, numberGroup, systemShareOperate);
    group.operate = zoneAccessPermitted(zone, numberGroup, systemShareOperate);

    const groupEvents = rtm[`${numberGroup}x${zone}`] || [];
    const elementEvents = [];

    //

    for (const item of group.elements!) {

      const element = item.id!;
      const events = rtm[`${numberGroup}x${zone}x${element}`] || [];


      if (!("properties" in item)) item.properties = item.properties1;

      elementEvents.push(...events);

      item.rtm = events.length ? events : groupEvents;
      item.properties1 = groupProperties;

      if (numberGroup === NumberGroup.Zone) {
        item.properties1 = propertyNotOperable(item.properties1, item.properties || 0);
      }

      item.eventKindType = item.rtm
        .map((l: SystemRTM) => l.eventKindType!)
        .sort(eventPriorityCompare)
        .shift();
    }

    group.rtm = groupEvents.length ? groupEvents : elementEvents;
    group.eventKindType = group.rtm
      .map((l: SystemRTM) => l.eventKindType!)
      .sort(eventPriorityCompare)
      .shift();
  }

  return setup;
};


export const useFetchSystemPlans = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest): Promise<PaginatedResult<SystemPlan> | SystemPlan[]> =>
  {
    const topic = `${ReactTopics.systemPlans.withParams({ systemId })}`;
    // console.log("useFetchSystemPlans: useCallback", topic, systemId, filterCriteria);
    return provider.fetch<PaginatedResult<SystemPlan> | SystemPlan[]>(
      filterCriteria,
      topic,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useSystemPlan = (systemId: string, planId: string) =>
{

  if (!planId) {
    // console.error("useSystemPlan: called with falsy planId!!!", planId);
    // planId = "11111111-2222-3333-4444-555555555555"; /// bogus-id-so-we-dont-load-all if planId is falsy
  }

  const topic = `${ReactTopics.systemPlan.withParams({ systemId, planId })}`;

  // console.log("useSystemPlan: called with", systemId, planId, topic);

  const systemPlan = useTopicData<SystemPlan>(
    topic,
    {} as SystemPlan
  );

  // console.log("useSystemPlan: systemPlan", systemId, planId, systemPlan);

  return systemPlan;
};

// // TODO: remove unused code when everything works
// // TODO: find all legacy uses and deal with them
// export const useSystemPlans = (systemId: string) =>
// {
//   console.error("!!! DEPRECATED !!! useSystemPlans call detected !!!");
//   return useTopicData<PaginatedResult<SystemPlan>>(
//     `${ReactTopics.systemPlan.withParams({ systemId })}`,
//     { currentPage: 0, pageSize: 10, results: [], totalCountOfEntries: 0, totalCountOfPages: 0 }
//   );
// };
// export const useSystemPlans = (systemId: string) => {
//   const xxx = useTopicData<SystemPlan[]>(
//     `${ReactTopics.systemPlan.withParams({ systemId })}`,
//     []
//   );
//   const y = (xxx as any).results;
//   return (y || []) as SystemPlan[];
// };


export const useSystemPlansWithoutImagesV2 = (systemId: string) =>
{
  const xxx = useTopicData<SystemPlan[]>(
    `${ReactTopics.systemPlans.withParams({ systemId })}`,
    []
  );
  return xxx;
};


export const useSystemPlansWithoutImages = (systemId: string) =>
{
  const [plans, setPlans] = useState<SystemPlan[]>([]);
  const fetchPlans = useFetchSystemPlans();

  useEffect(() =>
  {
    async function fetchData()
    {
      // WARNING: the excluded fields must be sent as a JSON encoded string atm!
      const fetchedPlans = await fetchPlans(systemId, { excludedFields: JSON.stringify(["image"]), noPaginate: true });
      // const fetchedPlans = await fetchPlans(systemId, { noPaginate: true });

      // console.log("useSystemPlansWithoutImages: useEffect: fetchData: fetchedPlans", fetchedPlans);
      if (!Array.isArray(fetchedPlans)) {
        console.error("useSystemPlansWithoutImages: fetchedPlans should be an array, but is:", typeof (fetchedPlans), fetchedPlans);
      }
      else {
        setPlans(fetchedPlans);
      }
      return;


      // // TODO: remove unused code when everything works
      // // TODO: find all use cases where the caller assumes the existence of the image urls; fix these; then remove dummy code
      // // apply dummy image so Plan.tsx does not crash
      // const DUMMY_IMAGE = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAHElEQVQI12P4//8/w38GIAXDIBKE0DHxgljNBAAO9TXL0Y4OHwAAAABJRU5ErkJggg==";
      // const results = [...(fetchedPlans as any)];
      // for (let i = 0; i < results.length; i++) {
      //   results[i].image = DUMMY_IMAGE;
      // }
      // setPlans(results);
    }

    if (systemId) {
      fetchData().catch(floatingPromiseHandler(import.meta.url, useSystemPlansWithoutImages.name));
    }
    else {
      console.warn("useSystemPlansWithoutImages: useEffect: was called with falsy systemId");
    }

    // return (() =>
    // {
    //   console.log("useSystemPlansWithoutImages: useEffect: cleanup function");
    // });

  }, [systemId, fetchPlans, setPlans]);

  // console.log("useSystemPlansWithoutImages: returning plans", plans);

  return plans;
};

export const useSystemDocumentation = (systemId: string) =>
{
  return useTopicData<OutputStructure>(
    `${ReactTopics.systemDocumentation.withParams({ systemId })}`,
    {} as OutputStructure
  );
};

type Dispose = (() => void);

const getSystemIdList = (list: System[]) => list.map(system => system.id);




export const useMySystems = (filterCriteria: PaginationRequest, ...args: any[]) =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  // useState without callback
  const [systemsResult, setSystemsResult] = useState<PaginatedResult<System>>({
    currentPage: 0,
    pageSize: 0,
    results: [],
    totalCountOfEntries: 0,
    totalCountOfPages: 0,
  });

  const handleSystem = useCallback((systemReceived: System) =>
  {
    setSystemsResult((result: PaginatedResult<System>) =>
    {
      const updatedResults = result.results.map((system: System) =>
      {
        return system.id === systemReceived.id ? systemReceived : system;
      });

      return { ...result, results: updatedResults };
    });
  }, []);

  const handleResult = useCallback(
    (result: PaginatedResult<System>): Dispose =>
    {

      setSystemsResult((previousResult: PaginatedResult<System>) =>
      {
        return result;
      });

      result.results.forEach((system: System) =>
      {
        const topic = ReactTopics.system.withParams({ systemId: system.id! });
        provider.subscribe(`${topic}`, handleSystem);
      });

      // return the cleanup function for what we just subscribed to
      return (() =>
      {
        result.results.forEach((system: System) =>
        {
          const topic = ReactTopics.system.withParams({ systemId: system.id! });
          provider.unsubscribe(`${topic}`, handleSystem);
        });
      });
    },
    [handleSystem, provider]
  );

  const doRequest = useCallback(
    async (filterCriteria: PaginationRequest) =>
    {
      return handleResult(
        await provider.fetch<PaginatedResult<System>>(
          filterCriteria,
          `${ReactTopics.mySystems}`,
          loadingToggle
        )
      );
    },
    [handleResult, loadingToggle, provider]
  );

  useEffect(() =>
  {
    let disposed = false;
    let cleanup: (() => void) | undefined;

    (async () =>
    {
      const dispose = await doRequest(filterCriteria);
      if (!disposed) {
        cleanup = dispose;
      } else {
        // useEffect already cleaned up before async resolved -> call immediately
        dispose();
      }
    })();

    return () =>
    {
      disposed = true;
      if (cleanup) cleanup();
    };
  }, [filterCriteria, doRequest]);

  return systemsResult;
};




export const useMySystemsDomenicVariation = (filterCriteria: PaginationRequest, intervalDelay?: number) =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  const previousSystemsIds = useRef<System["id"][]>([]);

  // useState without callback
  const [systemsResult, setSystemsResult] = useState<PaginatedResult<System>>({
    currentPage: 0,
    pageSize: 0,
    results: [],
    totalCountOfEntries: 0,
    totalCountOfPages: 0,
  });

  const handleSystem = useCallback((systemReceived: System) =>
  {
    setSystemsResult((result: PaginatedResult<System>) =>
    {
      const updatedResults = result.results.map((system: System) =>
      {
        return system.id === systemReceived.id ? systemReceived : system;
      });

      return { ...result, results: updatedResults };
    });
  }, []);

  const isEqualEventSort = useCallback((results: System[], previousIdList: System["id"][]) =>
  {
    if (!previousIdList.length || !results.length) {
      return false;
    };

    const currentResultIds = getSystemIdList(results);

    return currentResultIds.every((id, index) => id === (previousIdList[index] || ""));
  }, []);

  const handleResult = useCallback(
    (result: PaginatedResult<System>, refresh: boolean): Dispose =>
    {
      if (refresh) {
        const equal = isEqualEventSort(result.results, previousSystemsIds.current);
        if (equal) return () => { };
      }

      setSystemsResult((previousResult: PaginatedResult<System>) =>
      {
        previousSystemsIds.current = getSystemIdList(previousResult.results);
        return result;
      });

      result.results.forEach((system: System) =>
      {
        const topic = ReactTopics.system.withParams({ systemId: system.id! });
        provider.subscribe(`${topic}`, handleSystem);
      });

      // return the cleanup function for what we just subscribed to
      return (() =>
      {
        result.results.forEach((system: System) =>
        {
          const topic = ReactTopics.system.withParams({ systemId: system.id! });
          provider.unsubscribe(`${topic}`, handleSystem);
        });
      });
    },
    [handleSystem, isEqualEventSort, provider]
  );

  const doRequest = useCallback(
    async (filterCriteria: PaginationRequest, refresh: boolean = false) =>
    {
      return handleResult(
        await provider.fetch<PaginatedResult<System>>(
          filterCriteria,
          `${ReactTopics.mySystems}`,
          refresh ? undefined : loadingToggle
        ),
        refresh
      );
    },
    [handleResult, loadingToggle, provider]
  );
  // If the user changes the filter during the active interval, the current interval will be deleted and a new one will be created.
  // If the filter criteria don´t change, an interval will be created and the list will reload after the time.
  const intervalCallback = useCallback(() =>
  {
    doRequest(filterCriteria, true);
  }, [filterCriteria, doRequest]);

  useInactiveInterval(intervalCallback, intervalDelay || null);


  useEffect(() =>
  {
    let disposed = false;
    let cleanup: (() => void) | undefined;

    (async () =>
    {
      const dispose = await doRequest(filterCriteria);
      if (!disposed) {
        cleanup = dispose;
      } else {
        // useEffect already cleaned up before async resolved -> call immediately
        dispose();
      }
    })();

    return () =>
    {
      disposed = true;
      if (cleanup) cleanup();
    };
  }, [filterCriteria, doRequest]);

  return systemsResult;
};

export const useModifySystem = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: System) =>
    provider.modify(
      data,
      `${ReactTopics.system.withParams({ systemId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemDetails = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: SystemDetails) =>
    provider.modify(
      data,
      `${ReactTopics.systemDetails.withParams({ systemId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemMigrateRequest = () =>
{
  return useCallback(async (systemId: string, data: SystemMigrateRequest) =>
  {
    return null;
  }, []);

  // const loadingToggle = useLoadingIndicator();
  // const provider = useWebsocket();

  // return useCallback(async (systemId: string, data: SystemMigrateRequest) =>
  //   provider.modify(
  //     data,
  //     `${ReactTopics.systemMigrateRequest.withParams({ systemId })}`,
  //     loadingToggle
  //   ), [provider, loadingToggle]);
};

export const useModifySystemNotifications = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: SystemNotifications) =>
    provider.modify(
      data,
      `${ReactTopics.systemNotifications.withParams({ systemId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemLicense = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: SystemLicense) =>
    provider.modify(
      data,
      `${ReactTopics.systemLicense.withParams({ systemId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemPermissions = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: SystemPermissions) =>
    provider.modify(
      data,
      `${ReactTopics.systemPermissions.withParams({ systemId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemShare = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, userId: string, data: Partial<SystemShare>) =>
    provider.modify(
      data,
      `${ReactTopics.systemShare.withParams({ systemId, userId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemShareDisplay = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, userId: string, data: SystemShareDisplay) =>
    provider.modify(
      data,
      `${ReactTopics.systemShareDisplay.withParams({ systemId, userId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemShareNotifications = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (
    systemId: string,
    userId: string,
    data: SystemShareNotifications
  ) =>
    provider.modify(
      data,
      `${ReactTopics.systemShareNotifications.withParams({ systemId, userId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemShareOperate = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, userId: string, data: SystemShareOperate) =>
    provider.modify(
      data,
      `${ReactTopics.systemShareOperate.withParams({ systemId, userId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemMaintenance = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: SystemMaintenanceReport) =>
    provider.modify(
      data,
      `${ReactTopics.systemMaintenance.withParams({ systemId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemElement = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (
    systemId: string,
    numberGroup: number,
    groupId: number,
    id: number,
    data: SystemElement
  ) =>
    provider.modify(
      data,
      `${ReactTopics.systemElement.withParams({ systemId, groupId, numberGroup, id })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemPlan = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, data: SystemPlan, planId: string) =>
    provider.modify<SystemPlan, SystemPlan[]>(
      data,
      `${ReactTopics.systemPlan.withParams({ systemId, planId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemTextMessages = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, data: Partial<SystemTextMessagesDto>, id: string) =>
    provider.modify(
      data,
      `${ReactTopics.systemTextMessages.withParams({ systemId, id })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useModifySystemSpecification = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, data: SystemSpecificationDTO) =>
  {
    provider.modify(
      data,
      `${ReactTopics.systemSpecification.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useDeleteSystemPlan = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, planId: string) =>
    provider.delete(
      `${ReactTopics.systemPlan.withParams({ systemId, planId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useDeleteSystemTextMessages = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, id: string) =>
    provider.delete(
      `${ReactTopics.systemTextMessages.withParams({ systemId, id })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useDeleteSystemShare = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, userId: string) =>
    provider.delete(
      `${ReactTopics.systemShare.withParams({ systemId, userId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

// TODO: deprecated, remove once rpcs are released
// export const useCreateSystemSetupCSV = () =>
// {
//   const loadingToggle = useLoadingIndicator();
//   const [presentToast] = useIonToast();
//   const provider = useWebsocket();

//   return useCallback(async (
//     systemId: string,
//     zones: File,
//     actuations: File
//   ): Promise<SystemSetup> =>
//     new Promise((resolve, reject) =>
//     {
//       const actuationsReader = new FileReader();
//       const zonesReader = new FileReader();

//       actuationsReader.onload = async () =>
//       {
//         // ARGL!!!!!!!!!!!!!!!!!!!
//         // substr 37?????? base64 encoding
//         const importActuationsCSV = (
//           actuationsReader.result as string
//         ).substring(37);
//         const importZonesCSV = (zonesReader.result as string).substring(37);

//         try {
//           const result = (await provider.create(
//             { importActuationsCSV, importZonesCSV },
//             `${ReactTopics.systemSetup.withParams({ systemId })}`
//           )) as SystemSetup;
//           resolve(result);
//         } catch (e: any) {
//           presentToast(e as string, 3000);
//           reject(e);
//         }
//         loadingToggle(false);
//       };

//       zonesReader.onload = async () =>
//       {
//         actuationsReader.readAsDataURL(actuations);
//       };

//       loadingToggle(true);
//       zonesReader.readAsDataURL(zones);
//     }), [provider, loadingToggle]);
// };

// TODO: limit?
export const useCreateSystemSetup = () =>
{
  const loadingToggle = useLoadingIndicator();
  const [presentToast] = useIonToast();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: File): Promise<SystemSetup> =>
    new Promise((resolve, reject) =>
    {
      const reader = new FileReader();

      reader.onload = async () =>
      {
        const base64 = (reader.result as string).substring(37);

        try {
          const result = (await provider.create(
            { importXMLC: base64 },
            `${ReactTopics.systemSetup.withParams({ systemId })}`
          )) as SystemSetup;
          resolve(result);
        } catch (e: any) {
          presentToast(e as string, 3000);
          reject(e);
        }
        loadingToggle(false);
      };

      loadingToggle(true);
      reader.readAsDataURL(data);
    }), [provider, loadingToggle]);
};

export const useRemoveSystem = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string) =>
    provider.delete(
      `${ReactTopics.system.withParams({ systemId })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useCreateSystem = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (name: string, systemType: SystemType, customerId?: string) =>
  {
    const system: System = { name, systemType, customerId } as System;
    return await provider.create(
      system,
      `${ReactTopics.systemCreate}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useCreateSystemPlan = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: { floor: number; }) =>
  {
    console.info("system.ts: useCreateSystemPlan: about to create plan:", systemId, data);
    const result = await provider.create(
      data,
      `${ReactTopics.systemPlan.withParams({ systemId })}`,
      loadingToggle
    );
    console.info("system.ts: useCreateSystemPlan: created plan:", systemId, result);

    // TODO: returned type seems wrong!
    return result;
  }, [provider, loadingToggle]);
};

export const useCreateSystemShare = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, userId: string) =>
  {
    const systemShare: any = { systemId, userId };
    return await provider.create(
      { type: SystemShareType.EXTENDED } as unknown as SystemShare,
      `${ReactTopics.systemShare.withParams(systemShare)}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useCreateSystemTextMessages = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: Partial<SystemTextMessagesDto>) =>
  {
    return await provider.create(
      data,
      `${ReactTopics.systemTextMessages.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useCreateSystemPSU = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, panelId: number, data: PSU) =>
  {
    return await provider.create(
      data,
      `${ReactTopics.systemPSU.withParams({ systemId, panelId, boardId: null })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useModifySystemPSU = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (
    systemId: string,
    panelId: number,
    boardId: number,
    data: PSU
  ) =>
  {
    return await provider.modify(
      data,
      `${ReactTopics.systemPSU.withParams({ systemId, panelId, boardId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export type SystemOperationConfirmation = {
  code: number;
  message: string;
};

export const useOperateSystem = () =>
{
  const loadingToggle = useLoadingIndicator();
  //const [presentToast] = useIonToast();
  const alert = useAlert();
  const { translate: t } = useL10n();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, data: SystemOperate) =>
  {
    try {
      const systemOperate = await provider.operate<
        SystemOperate,
        SystemOperate
      >(
        data,
        `${ReactTopics.systemOperate.withParams({ systemId })}`,
        loadingToggle
      );

      if (!isSuccess(systemOperate.result!)) {
        throw getResult(systemOperate.result!);
      }

      return systemOperate;
    } catch (e: any) {
      //presentToast(e as string, 3000);

      if (e !== "TIMEOUT_QUEUE") alert({
        title: t("[SID_DLG_NOTE_OPER_FAILED]"),
        message: t(`OPER_RESULT_${e}`),
        buttonTitle: t("COM_TEMPLATES_TEMPLATE_CLOSE"),
      });
    }
  }, [provider, loadingToggle]);
};

export const useFetchSystemEvents = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<SystemEvents>>(
      filterCriteria,
      `${ReactTopics.systemEvents.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export type MaintenanceLogItem = {
  zone: number;
  element: number;
  elementText: string;
  groupText: string;
  eventKindType: number;
  numberGroup: NumberGroup;
  softwareDeviceType: number;
  timestamp: number;
  commentId?: string;
  commentText?: string;
  commentImage?: string;
  commentCreated?: Date;
};

export const useReportSystemMaintenanceLog = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, query: SortedRequest) =>
  {
    return provider.fetch<MaintenanceLogItem[]>(
      query,
      `${ReactTopics.reportMaintenanceLog.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useFetchSystemList = (noLoadingIndicator: boolean = false) =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<System>>(
      filterCriteria,
      `${ReactTopics.mySystems}`,
      noLoadingIndicator ? () => { } : loadingToggle
    );
  }, [provider, loadingToggle, noLoadingIndicator]);
};

export const useSystemDisplayFilter = (systemId: string) =>
{
  const [display, setDisplay] = useState(true);

  const fetchSystem = useFetchSystemList(true);

  useEffect(() =>
  {
    if (systemId)
      fetchSystem({ id: systemId }).then((result) =>
      {
        const system = result.results[0];
        setDisplay(Boolean(system && !system.filterOnClient));
      });
  }/* , [systemId, fetchSystem] */);

  return display;
};

export const useRedirectIfSystemUnavailable = (
  systemId: string,
  reEvaluate: number = 10000
) =>
{

  // const fetchSystem = useFetchSystemList(true);
  // const { generate } = useNamedRoutes();
  // const router = useIonRouter();

  // useEffect(() =>
  // {
  //   let timeout: number = 0;

  //   const evaluate = async () =>
  //   {
  //     if (systemId) {

  //       fetchSystem({ id: systemId }).then((result) =>
  //       {
  //         const system = result.results[0];
  //         if (!system || system.filterOnClient) {
  //           router.push(generate("systems"));
  //         }
  //       }).catch(floatingPromiseHandler(
  //         import.meta.url,
  //         useRedirectIfSystemUnavailable.name,
  //         `fetchSystem failed for systemId: ${systemId}`
  //       ));

  //       if (reEvaluate) {
  //         timeout = window.setTimeout(evaluate, reEvaluate);
  //       }
  //     }
  //   };

  //   evaluate();

  //   return () => clearTimeout(timeout);

  // });
};

export const useTypeAheadSystemList = () =>
{
  const provider = useWebsocket();

  return useCallback(async (filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<System>>(
      filterCriteria,
      `${ReactTopics.mySystems}`
    );
  }, [provider]);
};

export const useTypeAheadFetchSystemSystemTextMessages = () =>
{
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<SystemTextMessages>>(
      filterCriteria,
      `${ReactTopics.systemTextMessages.withParams({ systemId })}`
    );
  }, [provider]);
};

export const useFetchSystemMeasurement = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<SystemMeasurements>>(
      filterCriteria,
      `${ReactTopics.systemMeasurements.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useFetchSystemShares = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<SystemShare>>(
      filterCriteria,
      `${ReactTopics.systemShares.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useFetchSystemTextMessages = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    console.log(`${ReactTopics.systemTextMessages.withParams({ systemId })}`);
    return provider.fetch<PaginatedResult<SystemTextMessages>>(
      filterCriteria,
      `${ReactTopics.systemTextMessages.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useFetchSystemTextMessagesLog = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<SentTextMessage>>(
      filterCriteria,
      `${ReactTopics.systemTextMessagesLog.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useFetchSystemPSUs = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<PSU>>(
      filterCriteria,
      `${ReactTopics.systemPSU.withParams({ systemId, panelId: null, boardId: null })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useFetchSystemPanels = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    return provider.fetch<PaginatedResult<SystemPanel>>(
      filterCriteria,
      `${ReactTopics.systemPanel.withParams({ systemId })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useCreateSystemElementComment = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (data: {
    systemId: string;
    numberGroup: number;
    element: number;
    zone: number;
    text: string;
    image?: string;
    timestamp?: number;
  }) =>
  {
    return await provider.create(
      data,
      `${(ReactTopics as any).systemElementComment.withParams({ id: null })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

type SystemElementComment = Record<string, any>;

export const useModifySystemElementComment = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (systemId: string, id: string, data: SystemElementComment) =>
    provider.modify(
      data,
      `${(ReactTopics as any).systemElementComment.withParams({ systemId, id })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};

export const useDeleteSystemElementComment = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, id: string) =>
  {
    return provider.delete(
      `${ReactTopics.systemElementComment.withParams({ systemId, id })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useFetchSystemElementComment = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (
    systemId: string,
    filterCriteria: PaginationRequest,
    filterParams: Record<string, any> = {}
  ) =>
  {
    return provider.fetch<PaginatedResult<any>>(
      { systemId, ...filterCriteria },
      `${ReactTopics.systemElementComment.withParams({ id: null })}`,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};

export const useCreateSystemElement = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return useCallback(async (
    systemId: string,
    numberGroup: number,
    groupId: number,
    data: SystemElementForm
  ) =>
    provider.create(
      data,
      `${ReactTopics.systemElement.withParams({ systemId, groupId, numberGroup })}`,
      loadingToggle
    ), [provider, loadingToggle]);
};
/* export const useModifySystemElement = () => {
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();

  return async (
    systemId: string,
    numberGroup: number,
    groupId: number,
    data: SystemElementForm
  ) =>
    provider.create(
      data,
      `${ReactTopics.systemElement.withParams({ systemId, groupId, numberGroup })}`,
      loadingToggle
    );
}; */

export const useFetchSystemElements = () =>
{
  const loadingToggle = useLoadingIndicator();
  const provider = useWebsocket();
  return useCallback(async (systemId: string, filterCriteria: PaginationRequest) =>
  {
    const path = `${ReactTopics.systemElement.withParams({ systemId, groupId: null, id: null, numberGroup: null })}`;
    return provider.fetch<PaginatedResult<SystemElement>>(
      filterCriteria,
      path,
      loadingToggle
    );
  }, [provider, loadingToggle]);
};
